/**
 * Image History Component
 * Displays list of image generation jobs
 * Validates: Requirements 16.5
 */
import { ImageJob } from '../../stores/imageStore';
import { ImageJobCard } from './ImageJobCard';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface ImageHistoryProps {
  jobs: ImageJob[];
  currentJobId: string | null;
  isLoading: boolean;
  onSelectJob: (jobId: string) => void;
}

export function ImageHistory({
  jobs,
  currentJobId,
  isLoading,
  onSelectJob,
}: ImageHistoryProps) {
  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse grid gap-4 md:grid-cols-3 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-800 rounded-lg aspect-square" />
          ))}
        </div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="p-8 text-center">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
          style={{
            backgroundColor: 'rgba(255,255,255,0.03)',
            border: `1px solid ${THEME.border}`,
          }}
        >
          <Icons.Image size={32} style={{ color: THEME.textMuted }} />
        </div>
        <h3 className="text-lg font-medium mb-2" style={{ color: THEME.textMain }}>
          No images yet
        </h3>
        <p className="text-sm" style={{ color: THEME.textSecondary }}>
          Generate your first image using the form above.
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      <h3
        className="text-sm font-semibold uppercase tracking-wider"
        style={{ color: THEME.textMuted }}
      >
        Generation History
      </h3>
      <div className="grid gap-4 grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
        {jobs.map((job) => (
          <ImageJobCard
            key={job.id}
            job={job}
            isSelected={job.id === currentJobId}
            onClick={() => onSelectJob(job.id)}
          />
        ))}
      </div>
    </div>
  );
}
